import { useEffect, useRef, useState } from 'react';

/**
 * 화면을 닫았다가 다시 열어도 **닫기 전 상태가 그대로** 남는 useState (현장 원칙 2026-09-28:
 * "특별한 요구가 없으면 각 화면을 닫았다가 다시 열면 닫기 전 상태가 재현되도록").
 *
 * 앱이 떠 있는 동안 메모리에만 둔다 — 새로고침하면 처음 상태로 돌아간다. 검색 결과처럼 서버 데이터를
 * 담는 값도 넣을 수 있어 브라우저 저장소(용량·직렬화 문제)는 쓰지 않는다.
 * 키는 `화면:값` 처럼 화면마다 겹치지 않게 짓는다. 로그아웃하면 clearPersistentState() 로 비운다.
 */
const memory = new Map<string, unknown>();

export function usePersistentState<T>(key: string, initial: T | (() => T)) {
  const [value, setValue] = useState<T>(() => (memory.has(key)
    ? (memory.get(key) as T)
    : (typeof initial === 'function' ? (initial as () => T)() : initial)));
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => { memory.set(keyRef.current, value); }, [value]);
  return [value, setValue] as const;
}

/** 남겨 둔 화면 상태를 모두(또는 앞머리가 같은 것만) 지운다 — 로그아웃·사용자 전환 때 */
export function clearPersistentState(prefix = ''): void {
  Array.from(memory.keys()).forEach((k) => { if (k.startsWith(prefix)) memory.delete(k); });
}
